import * as Sentry from '@sentry/react';

const SENSITIVE_KEY_SNIPPETS = [
    'password',
    'pwd',
    'passwd',
    'otp',
    'cvv',
    'cvc',
    'cardnumber',
    'card',
    'ssn',
    'token',
    'access_token',
    'refresh_token',
    'id_token',
    'secret',
    'client_secret',
    'api_key',
    'apikey',
    'authorization',
    'auth',
    'bearer',
    'session',
    'cookie',
];

const REDACTED = '[Filtered]';

function isSensitiveKey(key) {
    const lower = String(key).toLowerCase();
    return SENSITIVE_KEY_SNIPPETS.some((snippet) => lower.includes(snippet));
}

function scrubArray(value, seen) {
    if (seen.has(value)) return seen.get(value);

    const clone = [];
    seen.set(value, clone);

    value.forEach((item) => {
        clone.push(scrub(item, seen));
    });

    return clone;
}

function scrubObject(value, seen) {
    if (seen.has(value)) return seen.get(value);

    const clone = {};
    seen.set(value, clone);

    for (const [key, val] of Object.entries(value)) {
        clone[key] = isSensitiveKey(key)
            ? REDACTED
            : scrub(val, seen);
    }

    return clone;
}

function scrub(value, seen) {
    if (Array.isArray(value)) {
        return scrubArray(value, seen);
    }

    // Also traverse null-prototype objects (e.g. Object.create(null)) — they have no
    // .constructor, so checking `=== Object` alone would skip and leave them unredacted.
    if (value && typeof value === 'object' && (!value.constructor || value.constructor === Object)) {
        return scrubObject(value, seen);
    }

    return value;
}

function scrubBreadcrumb(breadcrumb) {
    if (breadcrumb?.data) {
        breadcrumb.data = scrub(breadcrumb.data, new WeakMap());
    }

    return breadcrumb;
}

function scrubEvent(event) {
    const seen = new WeakMap();

    ['request', 'extra', 'contexts', 'user', 'breadcrumbs'].forEach((field) => {
        if (event[field]) {
            event[field] = scrub(event[field], seen);
        }
    });

    return event;
}

Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV_VAR,

    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,

    // Never auto-populate user.* fields, and never send raw HTTP bodies — scrub() only
    // redacts known sensitive keys inside object graphs, it can't sanitize a stringified
    // body (e.g. URL-encoded form data or a raw JSON string), so bodies must be blocked outright.
    dataCollection: {
        userInfo: false,
    },

    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.httpClientIntegration(),
        Sentry.breadcrumbsIntegration({
            // Disable console.log breadcrumbs
            console: false,
        }),
    ],

    // Capture 100% of transactions for performance monitoring.
    tracesSampleRate: 1.0,
});